// ProjectCreateChat.tsx
import {
  Box,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { IoArrowUpCircleOutline } from 'react-icons/io5';
import { MdOutlinePeople, MdOutlinePerson } from 'react-icons/md';
import { TbWorld } from 'react-icons/tb';
import {
  ConversationType,
  DtType,
  PerspectiveBaseInfoFragment,
  ProjectDocument,
  useGetAiMessageResponseMutation,
  useSendAiMessageMutation,
  useStartConversationMutation,
  useProfileDropdownQuery,
  ProfileDropdownDocument,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import CreateChatLoading from './CreateChatLoading';
import DemographicSelector from './DemographicSelector';
import PersonaSelector from './PersonaSelector';
import { ModelCardData } from './ProjectView';

interface ProjectCreateChatProps {
  setCreateNew: (bool: boolean) => void;
  allTwins: { id: string; name: string }[];
  topic: string;
  setConversationId: (id: string) => void;
  projectId: string;
  dtType: DtType;
  perspectives: PerspectiveBaseInfoFragment[];
  modelCardData?: ModelCardData;
  chatInitialType: ConversationType;
}

interface PersonaType {
  id: string;
  name: string;
}

export const ProjectCreateChat = ({
  setCreateNew,
  allTwins,
  topic,
  setConversationId,
  projectId,
  dtType,
  perspectives,
  modelCardData,
  chatInitialType,
}: ProjectCreateChatProps) => {
  const [createConversation, { loading: createConversationLoading }] =
    useStartConversationMutation();
  const [sendMessage, { loading: sendMessageLoading }] =
    useSendAiMessageMutation();
  const [getResponse, { loading: getResponseLoading }] =
    useGetAiMessageResponseMutation();
  const { data: userData } = useProfileDropdownQuery();
  const canSendMessage = userData?.user?.canSendMessage ?? true;
  const [activeMessage, setActiveMessage] = useState('');
  const [chatType, setChatType] = useState<ConversationType>(
    chatInitialType ?? ConversationType.Persona
  );
  const [selectedPersonas, setSelectedPersonas] = useState<PersonaType[]>([]);
  const [selectedDemographics, setSelectedDemographics] = useState<
    { key: string; value: string }[]
  >([]);

  // Handler for when selection changes
  const handleSelectionChange = useCallback((newSelection: PersonaType[]) => {
    // Make sure to create a new array to trigger state updates
    setSelectedPersonas([...newSelection]);
  }, []);

  const switchSelectionType = (type: ConversationType) => {
    handleSelectionChange([]);
    setChatType(type);
  };

  const getDemographicTwins = (): string[] => {
    if (!selectedDemographics || selectedDemographics.length === 0) {
      return [];
    }

    const demoTwins = [...perspectives]
      .filter((perspective) => perspective.topic === 'topic')
      .filter((perspective) => {
        const dt = perspective.digitalTwin;
        if (!dt) return false; // Custom agents don't have demographics

        if (
          dt.congressData &&
          Object(dt.congressData).hasOwnProperty(
            selectedDemographics[0].key
          )
        ) {
          let include = true;

          for (let i = 0; i < selectedDemographics.length; i++) {
            if (
              // @ts-ignore
              dt.congressData[
              selectedDemographics[0].key
              ] !== selectedDemographics[0].value
            ) {
              include = false;
            }
          }

          return include;
        }
        if (
          dt.moviesMediumFullData &&
          Object(dt.moviesMediumFullData).hasOwnProperty(
            selectedDemographics[0].key
          )
        ) {
          let include = true;

          for (let i = 0; i < selectedDemographics.length; i++) {
            if (
              // @ts-ignore
              dt.moviesMediumFullData[
              selectedDemographics[0].key
              ] !== selectedDemographics[0].value
            ) {
              include = false;
            }
          }

          return include;
        }
        if (
          dt.moviesSimpleData &&
          Object(dt.moviesSimpleData).hasOwnProperty(
            selectedDemographics[0].key
          )
        ) {
          let include = true;

          for (let i = 0; i < selectedDemographics.length; i++) {
            if (
              // @ts-ignore
              dt.moviesSimpleData[
              selectedDemographics[0].key
              ] !== selectedDemographics[0].value
            ) {
              include = false;
            }
          }

          return include;
        }

        return false;
      });

    return demoTwins.map((p) => p.digitalTwin!.id);
  };

  const handleDemographicChange = (
    demographics: { key: string; value: string }[]
  ) => {
    setSelectedDemographics(demographics);
  };

  const handleCreateAndSend = () => {
    if (activeMessage.length === 0) {
      throw new Error('Message empty');
    }

    createConversation({
      variables: {
        digitalTwinIds:
          chatType === ConversationType.Demographic
            ? getDemographicTwins()
            : selectedPersonas.map((p) => p.id),
        projectId,
        type: chatType,
        demographic:
          chatType === ConversationType.Demographic
            ? selectedDemographics.map((demo) => demo.value).join(', ')
            : undefined,
      },
      refetchQueries: [
        {
          query: ProjectDocument,
          variables: {
            id: projectId,
          },
        },
      ],
      onCompleted: (response) => {
        const conversationId = response.startConversation.id;
        sendMessage({
          variables: {
            message: activeMessage,
            conversationId,
          },
          refetchQueries: [{ query: ProfileDropdownDocument }],
          onCompleted: (data) => {
            getResponse({
              variables: {
                conversationId,
              },
              onCompleted: (data) => {
                if (data.getAIMessageResponse.messages) {
                  setConversationId(conversationId);
                  setCreateNew(false);
                }
              },
            });
          },
        });
      },
    });
  };

  return (
    <Flex
      width="full"
      height="full"
      flexDirection="column"
      gap={6}
      p={4}
      justifyContent="space-between"
      overflow="hidden"
    >
      <Stack spacing={3}>
        <Flex width="full" justifyContent="space-between" flexDirection="row">
          <div />
          <HelivanText variant="largeBody+">New Chat</HelivanText>
          <Box
            onClick={() => {
              setCreateNew(false);
            }}
            px="6px"
            py="2px"
            bg="white"
            border="0.5px solid #D0D0D0"
            borderRadius="4px"
            _hover={{ bg: HelivanColor.hover.lightBlue }}
            cursor="pointer"
          >
            <HelivanText variant="smallBody">Cancel</HelivanText>
          </Box>
        </Flex>

        <HStack spacing={1}>
          <HStack
            onClick={() => {
              switchSelectionType(ConversationType.Persona);
            }}
            bg={
              chatType === ConversationType.Persona
                ? HelivanColor.hover.lightBlue
                : undefined
            }
            spacing={1}
            _hover={{ bg: HelivanColor.hover.lightBlue }}
            px="6px"
            py="2px"
            borderRadius="4px"
            cursor="pointer"
          >
            <MdOutlinePerson size={16} color={HelivanColor.text.blueMid} />
            <HelivanText
              variant="largeBody"
              colorOverride={HelivanColor.text.blueDark}
            >
              1-1
            </HelivanText>
          </HStack>
          <HStack
            onClick={() => {
              switchSelectionType(ConversationType.Panel);
            }}
            bg={
              chatType === ConversationType.Panel
                ? HelivanColor.hover.lightBlue
                : undefined
            }
            spacing={1}
            _hover={{ bg: HelivanColor.hover.lightBlue }}
            px="6px"
            py="2px"
            borderRadius="4px"
            cursor="pointer"
          >
            <MdOutlinePeople size={16} color={HelivanColor.text.blueMid} />
            <HelivanText
              variant="largeBody"
              colorOverride={HelivanColor.text.blueDark}
            >
              Panel
            </HelivanText>
          </HStack>
          <HStack
            onClick={() => {
              switchSelectionType(ConversationType.Demographic);
            }}
            bg={
              chatType === ConversationType.Demographic
                ? HelivanColor.hover.lightBlue
                : undefined
            }
            spacing={1}
            _hover={{ bg: HelivanColor.hover.lightBlue }}
            px="6px"
            py="2px"
            borderRadius="4px"
            cursor="pointer"
          >
            <TbWorld size={16} color={HelivanColor.text.blueMid} />
            <HelivanText
              variant="largeBody"
              colorOverride={HelivanColor.text.blueDark}
            >
              Crowd
            </HelivanText>
          </HStack>
        </HStack>
        {/* Persona selector component */}
        {chatType === ConversationType.Demographic ? (
          <DemographicSelector
            dtType={dtType}
            type={chatType}
            maxSelections={6}
            onSelectionChange={handleDemographicChange}
          />
        ) : (
          <PersonaSelector
            onSelectionChange={handleSelectionChange}
            allPersonas={allTwins}
            type={chatType}
            initialSelected={
              modelCardData?.entityId
                ? {
                  id: modelCardData.entityId,
                  name: modelCardData.entityName,
                }
                : undefined
            }
          />
        )}
      </Stack>

      {(getResponseLoading ||
        sendMessageLoading ||
        createConversationLoading) && (
          <Flex
            width="full"
            justifyContent="center"
            alignItems="flex-start"
            height="fit-content"
          >
            <CreateChatLoading isLoading={true} size="sm" />
          </Flex>
        )}

      <Tooltip
        label="You're out of free credits. Contact info@helivan.io to pay for more. Credits update every week."
        isDisabled={canSendMessage}
        placement="top"
      >
        <InputGroup>
          <Input
            bg="white"
            px={5}
            py={4}
            width="full"
            borderRadius="12px"
            height="50px"
            borderWidth="1px"
            value={activeMessage}
            onChange={(e) => setActiveMessage(e.target.value)}
            placeholder={`Ask ${chatType === ConversationType.Persona ? 'me' : 'us'} about ${topic}.`}
            onKeyDown={(e) => {
              // Send message on Enter key without Shift key
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault(); // Prevent default to avoid new line
                handleCreateAndSend();
              }
            }}
            isDisabled={
              getResponseLoading ||
              sendMessageLoading ||
              createConversationLoading ||
              !canSendMessage ||
              (chatType === ConversationType.Demographic &&
                selectedDemographics.length === 0) ||
              (chatType === ConversationType.Panel &&
                selectedPersonas.length < 2) ||
              (chatType === ConversationType.Persona &&
                selectedPersonas.length === 0)
            }
          />

          <InputRightElement
            height="full"
            alignItems="center"
            onClick={
              getResponseLoading ||
                sendMessageLoading ||
                createConversationLoading ||
                !canSendMessage ||
                (chatType === ConversationType.Demographic &&
                  selectedDemographics.length === 0) ||
                (chatType === ConversationType.Panel &&
                  selectedPersonas.length < 2) ||
                (chatType === ConversationType.Persona &&
                  selectedPersonas.length === 0)
                ? undefined
                : () => {
                  handleCreateAndSend();
                }
            }
          >
            <Box
              opacity={
                getResponseLoading ||
                  sendMessageLoading ||
                  createConversationLoading ||
                  !canSendMessage ||
                  (chatType === ConversationType.Demographic &&
                    selectedDemographics.length === 0) ||
                  (chatType === ConversationType.Panel &&
                    selectedPersonas.length < 2) ||
                  (chatType === ConversationType.Persona &&
                    selectedPersonas.length === 0)
                  ? '50%'
                  : undefined
              }
              cursor={
                getResponseLoading ||
                  sendMessageLoading ||
                  createConversationLoading ||
                  !canSendMessage ||
                  (chatType === ConversationType.Demographic &&
                    selectedDemographics.length === 0) ||
                  (chatType === ConversationType.Panel &&
                    selectedPersonas.length < 2) ||
                  (chatType === ConversationType.Persona &&
                    (selectedPersonas.length === 0 || selectedPersonas.length > 1))
                  ? 'not-allowed'
                  : 'pointer'
              }
            >
              <IoArrowUpCircleOutline
                size={30}
                color={HelivanColor.text.blueDark}
              />
            </Box>
          </InputRightElement>
        </InputGroup>
      </Tooltip>
    </Flex>
  );
};
